import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Skeleton as AbyssSkeleton } from '@uhg-abyss/web/ui/Skeleton';
import { motion } from 'framer-motion';
import React from 'react';

/**
 * Skeleton
 *
 * Displays a roughed-in idea/placeholder of what the UI should look like while the data is fetching from the API.
 *
 * @param hasCipSource
 * @returns {Element}
 * @constructor
 */
export function Skeleton({ hasCipSource = false }) {
  return (
    <ErrorHandler location="src/pages/private/ActionPaths/View/layouts/Automatic/components/Skeleton/Skeleton.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid className="flexGrid" css={{ height: 450 }}>
          {hasCipSource
            ? [
                <Grid.Col
                  className="grow"
                  css={{ paddingTop: 'var(--abyss-space-lg)' }}
                  key="cip-InQueue"
                  span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
                >
                  <AbyssSkeleton height="100%" />
                </Grid.Col>,
                <Grid.Col
                  className="grow"
                  css={{ paddingTop: 'var(--abyss-space-lg)' }}
                  key="cip-ActionStatusCodes"
                  span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
                >
                  <AbyssSkeleton height="100%" />
                </Grid.Col>,

                <Grid.Col
                  className="grow"
                  css={{ paddingTop: 'var(--abyss-space-lg)' }}
                  key="cip-ActionTakenCodes"
                  span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
                >
                  <AbyssSkeleton height="100%" />
                </Grid.Col>,
              ]
            : [
                <Grid.Col
                  css={{ paddingTop: 'var(--abyss-space-lg)' }}
                  key="InQueue"
                  span={{ lg: '33.33%', md: '33.33%', sm: '33.33%', xs: '33.33%' }}
                >
                  <AbyssSkeleton height="100%" />
                </Grid.Col>,

                <Grid.Col
                  css={{ paddingTop: 'var(--abyss-space-lg)' }}
                  key="stats1"
                  span={{ lg: '66.66%', md: '66.66%', sm: '66.66%', xs: '66.66%' }}
                >
                  <div style={{ width: '100%' }}>
                    <Layout.Stack alignItems="left" grow space={Number(themeConfiguration?.theme?.space?.md) || 16}>
                      <AbyssSkeleton height={200} />
                      <AbyssSkeleton height={200} />
                    </Layout.Stack>
                  </div>
                </Grid.Col>,
              ]}
        </Grid>

        <Divider color="transparent" />

        <Grid css={{ height: 350 }}>
          <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="entranceCriteria" span={{ xs: '50%' }}>
            <AbyssSkeleton height="100%" />
          </Grid.Col>

          <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="exitCriteria" span={{ xs: '50%' }}>
            <AbyssSkeleton height="100%" />
          </Grid.Col>

          <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="remediationActions" span={{ xs: '100%' }}>
            <AbyssSkeleton height="100%" />
          </Grid.Col>
        </Grid>

        <Divider color="transparent" />

        {hasCipSource && (
          <Grid css={{ height: 225 }} key="stats2">
            <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="SourceRecordsRemediated" span={{ xs: '50%' }}>
              <AbyssSkeleton height="100%" />
            </Grid.Col>
            <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="RecordAgeInPath" span={{ xs: '50%' }}>
              <AbyssSkeleton height="100%" />
            </Grid.Col>
          </Grid>
        )}

        <Divider color="transparent" />

        <Grid css={{ height: 350 }}>
          <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} key="notes" span={{ xs: '50%' }}>
            <AbyssSkeleton height="100%" />
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
